import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { reactive, ref, watch } from 'vue'
import { ToastInterface, useToast } from 'vue-toastification'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { PaginatedResponse } from '@/interfaces/paginatedResponse'

import { User } from '@/modules/users/interfaces/user'

interface Props {
	immediate?: boolean
	errorHandling?: boolean
}

interface Params {
	page?: number | string
	filters?: Filters
	errorHandling?: boolean
}

interface CreateParams {
	name: string
	lastname: string
	birthDate: string
	gender: string
	roleId: number
	companyId?: number
	email: string
}

interface Filters {
	search?: string
	role?: string | number
	status?: string
	sort?: string
	sortBy?: string
}

interface UpdateParams extends CreateParams {
	id: number
}

const getInternalUsers = async (
	params?: Params
): Promise<PaginatedResponse<User>> => {
	const { data } = await axios.get(`/api/internal/users?page=${params?.page}`, {
		params: {
			search: params?.filters?.search,
			role: params?.filters?.role !== 'all' ? params?.filters?.role : undefined,
			status:
				params?.filters?.status !== 'all' ? params?.filters?.status : undefined,
			sortBy:
				params?.filters?.sortBy !== 'all' ? params?.filters?.sortBy : undefined,
			sortDirection:
				params?.filters?.sort !== 'all' ? params?.filters?.sort : undefined,
		},
		errorHandling: params?.errorHandling,
	} as CustomAxiosRequestConfig)

	return data
}

const createInternalUser = async (
	params: CreateParams,
	toast: ToastInterface
): Promise<User | undefined> => {
	try {
		const { data } = await axios.post('/api/internal/users', params)

		toast.success('User creado correctamente')

		return data
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const updateInternalUser = async (params: UpdateParams): Promise<User> => {
	const { data } = await axios.put(`/api/internal/users/${params.id}`, params)

	return data
}

const deleteInternalUser = async (id: number): Promise<void> => {
	await axios.delete(`/api/internal/users/${id}`)
}

const restoreInternalUser = async (id: number): Promise<void> => {
	await axios.post(`/api/internal/users/${id}/restore`)
}

const useInternalUsers = (props: Props) => {
	const { immediate = true, errorHandling = false } = props
	const page = ref<string | number>(1)
	const toast = useToast()

	const filters = reactive<Filters>({
		search: undefined,
		status: undefined,
		role: undefined,
		sortBy: 'all',
		sort: 'asc',
	})

	const { data, isLoading, refetch } = useQuery(
		['internalUsers'],
		() =>
			getInternalUsers({
				page: page.value,
				filters,
				errorHandling,
			}),
		{
			enabled: immediate,
		}
	)

	const setPage = (newPage: string | number) => {
		page.value = newPage
	}

	const debouncedSearchRefetch = useDebounceFn(() => {
		setPage(1)
		refetch()
	}, 400)

	watch(() => filters.search, debouncedSearchRefetch)

	const handleFilters = (p0: string, newFilters: Filters) => {
		filters.search = newFilters.search
		filters.status = newFilters.status
		filters.role = newFilters.role
	}

	const createMutation = useMutation((params: CreateParams) =>
		createInternalUser(params, toast)
	)

	const updateMutation = useMutation((params: UpdateParams) =>
		updateInternalUser(params)
	)

	const deleteMutation = useMutation((id: number) => deleteInternalUser(id))

	const restoreMutation = useMutation((id: number) => restoreInternalUser(id))

	return {
		data,
		isLoading,
		refetch,
		page,
		setPage,
		filters,
		handleFilters,

		createUserMutation: createMutation.mutate,
		createUserMutationAsync: createMutation.mutateAsync,
		createUserLoading: createMutation.isLoading,

		updateUserMutation: updateMutation.mutate,
		updateUserMutationAsync: updateMutation.mutateAsync,
		updateUserLoading: updateMutation.isLoading,

		deleteUserMutation: deleteMutation.mutate,
		deleteUserMutationAsync: deleteMutation.mutateAsync,
		deleteUserLoading: deleteMutation.isLoading,

		restoreUserMutation: restoreMutation.mutate,
		restoreUserMutationAsync: restoreMutation.mutateAsync,
		restoreUserLoading: restoreMutation.isLoading,
	}
}

export default useInternalUsers
